import React, { useMemo, useState } from "react";
import type { QuestionType } from "../../../services/api";
import type { ElementTemplateKey } from "../types";
import type { FormElementVisibilityMap } from "../lib/formElementVisibility";

interface FormElementItem {
  template: ElementTemplateKey;
  label: string;
}

interface FormElementVisibilityModalProps {
  questionTypes: QuestionType[];
  formElementItems: FormElementItem[];
  overrides: FormElementVisibilityMap;
  resolveDefault: (reference: {
    id?: string;
    name?: string;
    displayName?: string | null;
  }) => ElementTemplateKey[] | null;
  onSave: (next: FormElementVisibilityMap) => void;
  onClose: () => void;
}

const FormElementVisibilityModal: React.FC<FormElementVisibilityModalProps> = ({
  questionTypes,
  formElementItems,
  overrides,
  resolveDefault,
  onSave,
  onClose,
}) => {
  const [draft, setDraft] = useState<FormElementVisibilityMap>(() => ({
    ...overrides,
  }));
  const [selectedId, setSelectedId] = useState<string | null>(
    questionTypes.length > 0 ? String(questionTypes[0].id) : null
  );

  const allTemplates = useMemo(
    () => formElementItems.map((item) => item.template),
    [formElementItems]
  );

  const selectedType = useMemo(
    () =>
      questionTypes.find((type) => String(type.id) === selectedId) ?? null,
    [questionTypes, selectedId]
  );

  // Bir soru tipi için varsayılan (kod tanımlı) görünür şablonlar.
  // resolveDefault null dönerse "hepsi görünür" demektir.
  const defaultTemplatesFor = (type: QuestionType): ElementTemplateKey[] => {
    const resolved = resolveDefault(type);
    if (resolved === null) {
      return allTemplates;
    }
    const allowed = new Set(resolved);
    return allTemplates.filter((template) => allowed.has(template));
  };

  const effectiveTemplatesFor = (type: QuestionType): ElementTemplateKey[] => {
    const key = String(type.id);
    return draft[key] ?? defaultTemplatesFor(type);
  };

  const isCustomized = (type: QuestionType) => String(type.id) in draft;

  const selectedTemplates = useMemo<Set<ElementTemplateKey>>(() => {
    if (!selectedType) return new Set();
    return new Set(effectiveTemplatesFor(selectedType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType, draft, allTemplates]);

  const setSelectedList = (list: ElementTemplateKey[]) => {
    if (!selectedType) return;
    setDraft((prev) => ({ ...prev, [String(selectedType.id)]: list }));
  };

  const toggleTemplate = (template: ElementTemplateKey) => {
    if (!selectedType) return;
    const current = effectiveTemplatesFor(selectedType);
    const has = current.includes(template);
    const next = has
      ? current.filter((item) => item !== template)
      : [...current, template];
    setSelectedList(next);
  };

  const resetSelectedToDefault = () => {
    if (!selectedType) return;
    setDraft((prev) => {
      const next = { ...prev };
      delete next[String(selectedType.id)];
      return next;
    });
  };

  const selectAll = () => setSelectedList(allTemplates);
  const selectNone = () => setSelectedList([]);

  return (
    <div className="qe-modal" onClick={onClose}>
      <div className="qe-modal__backdrop" />
      <div
        className="qe-modal__content"
        onClick={(event) => event.stopPropagation()}
        style={{ width: "720px", maxWidth: "94vw", maxHeight: "84vh" }}
      >
        <div className="qe-modal__header">
          <h2>Form Elemanı Görünürlüğü</h2>
          <button
            className="qe-button qe-button--ghost qe-icon-button"
            type="button"
            onClick={onClose}
            aria-label="Kapat"
          >
            ×
          </button>
        </div>

        <div className="qe-modal__body" style={{ padding: 0 }}>
          <p
            className="qe-form__help"
            style={{ padding: "12px 16px 0", margin: 0 }}
          >
            Her soru tipinde palette hangi form elemanlarının görüneceğini
            seçin. Temel Öğeler (metin, resim, ses…) her tipte görünür ve bu
            listeye dahil değildir. Tercihleriniz bu tarayıcıda kalıcıdır.
          </p>

          {questionTypes.length === 0 ? (
            <div
              style={{
                padding: "32px 16px",
                textAlign: "center",
                color: "var(--qe-text-muted)",
              }}
            >
              Soru tipleri yüklenemedi.
            </div>
          ) : (
            <div
              className="qe-fev"
              style={{
                display: "grid",
                gridTemplateColumns: "220px 1fr",
                gap: 0,
                minHeight: 340,
                maxHeight: "58vh",
              }}
            >
              {/* Sol: soru tipleri */}
              <div
                className="qe-fev__types"
                style={{
                  borderRight: "1px solid var(--qe-border)",
                  overflowY: "auto",
                  padding: "8px",
                }}
              >
                {questionTypes.map((type) => {
                  const active = String(type.id) === selectedId;
                  return (
                    <button
                      key={type.id}
                      type="button"
                      onClick={() => setSelectedId(String(type.id))}
                      className="qe-fev__type"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        width: "100%",
                        textAlign: "left",
                        padding: "8px 10px",
                        marginBottom: 2,
                        borderRadius: 8,
                        border: "1px solid transparent",
                        background: active
                          ? "var(--qe-accent-soft, rgba(37,99,235,0.10))"
                          : "transparent",
                        color: "var(--qe-text)",
                        cursor: "pointer",
                        fontSize: 13,
                        fontWeight: active ? 600 : 400,
                      }}
                    >
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {type.displayName || type.name}
                      </span>
                      {isCustomized(type) ? (
                        <span
                          title="Özelleştirildi"
                          style={{
                            flex: "0 0 auto",
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: "var(--qe-accent)",
                          }}
                        />
                      ) : null}
                    </button>
                  );
                })}
              </div>

              {/* Sağ: seçili tipin form elemanları */}
              <div
                className="qe-fev__items"
                style={{ overflowY: "auto", padding: "12px 16px" }}
              >
                {selectedType ? (
                  <>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 8,
                        marginBottom: 10,
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {selectedType.displayName || selectedType.name}
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          type="button"
                          className="qe-button qe-button--ghost qe-button--small"
                          onClick={selectAll}
                        >
                          Tümü
                        </button>
                        <button
                          type="button"
                          className="qe-button qe-button--ghost qe-button--small"
                          onClick={selectNone}
                        >
                          Hiçbiri
                        </button>
                        <button
                          type="button"
                          className="qe-button qe-button--ghost qe-button--small"
                          onClick={resetSelectedToDefault}
                          disabled={!isCustomized(selectedType)}
                          title="Bu tipi kod tanımlı varsayılana döndür"
                        >
                          Varsayılana dön
                        </button>
                      </div>
                    </div>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                      }}
                    >
                      {formElementItems.map((item) => {
                        const checked = selectedTemplates.has(item.template);
                        return (
                          <label
                            key={item.template}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "7px 8px",
                              borderRadius: 8,
                              cursor: "pointer",
                              fontSize: 13,
                              background: checked
                                ? "var(--qe-accent-soft, rgba(37,99,235,0.06))"
                                : "transparent",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleTemplate(item.template)}
                            />
                            <span>{item.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </>
                ) : (
                  <div style={{ color: "var(--qe-text-muted)" }}>
                    Soldan bir soru tipi seçin.
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="qe-modal__footer">
          <div className="qe-modal__actions">
            <button
              type="button"
              className="qe-button qe-button--secondary"
              onClick={onClose}
            >
              İptal
            </button>
            <button
              type="button"
              className="qe-button qe-button--primary"
              onClick={() => onSave(draft)}
            >
              Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormElementVisibilityModal;
